import { APP_SCHEMA_VERSION, SCHEMA_VERSION_STORAGE_KEY } from '@/config/app-version';
import type { UserId } from '@/schemas/ids';
import { useRealtimeStore } from '@/stores/realtime-store';

function getLocalStorage(): Storage | undefined {
  try {
    const storage = Reflect.get(globalThis, 'localStorage') as Storage | undefined;
    if (!storage) {
      return undefined;
    }
    // Access a benign key to ensure the storage is usable (may throw in private contexts)
    storage.getItem(SCHEMA_VERSION_STORAGE_KEY);
    return storage;
  } catch {
    // localStorage unavailable or access denied
    return undefined;
  }
}

/**
 * Ensure realtime subscriptions are aligned with the current schema version.
 * Forces a resubscribe when the application version changes or when local storage
 * cannot be read (fallback to force reset).
 */
export async function ensureRealtimeSubscriptions(userId: UserId): Promise<void> {
  const realtimeStore = useRealtimeStore.getState();

  if (!userId) {
    throw new Error('userId is required to ensure realtime subscriptions');
  }

  const storage = getLocalStorage();
  if (!storage) {
    await realtimeStore.enableRealtime(userId, { force: true });
    return;
  }

  try {
    const storedVersion = storage.getItem(SCHEMA_VERSION_STORAGE_KEY);
    if (storedVersion !== APP_SCHEMA_VERSION) {
      await realtimeStore.resetRealtime(userId);
      storage.setItem(SCHEMA_VERSION_STORAGE_KEY, APP_SCHEMA_VERSION);
      return;
    }
  } catch (error) {
    console.warn('Failed to access schema version storage, forcing realtime reset', error);
    await realtimeStore.resetRealtime(userId);
    return;
  }

  await realtimeStore.enableRealtime(userId);
}
